// externals
import type { Reducer } from 'redux';

// libraries
import { NavigationActions } from '@makemydeal/dr-activities-common';

// utils
import { catalogToVehicle, mapMakes, mapModels, mapTrims, mapYears, incrementFetchCount, decrementFetchCount } from '../utils';
import { processAgentVehicleResponse, normalizeFilterSummary } from '../utils/agentUtils';
import { isSameVin, isVinInActiveInventory } from '../utils/aiInventoryFilter';

// types
import type { ChangeVehicleState } from '../types';
import type { IVehicle } from '@makemydeal/dr-platform-types';

// actions
import {
    CHANGE_VEHICLE_CANCEL,
    RESET_ON_ADD_NEW_SCENARIO,
    CLEAR_CATALOG_VEHICLES,
    FETCH_CATALOG_MAKES_FAILURE,
    FETCH_CATALOG_MAKES_REQUEST,
    FETCH_CATALOG_MAKES_SUCCESS,
    FETCH_CATALOG_MODELS_FAILURE,
    FETCH_CATALOG_MODELS_REQUEST,
    FETCH_CATALOG_MODELS_SUCCESS,
    FETCH_CATALOG_TRIMS_FAILURE,
    FETCH_CATALOG_TRIMS_REQUEST,
    FETCH_CATALOG_TRIMS_SUCCESS,
    FETCH_CATALOG_VEHICLES_FAILURE,
    FETCH_CATALOG_VEHICLES_REQUEST,
    FETCH_CATALOG_VEHICLES_SUCCESS,
    FETCH_CATALOG_YEARS_FAILURE,
    FETCH_CATALOG_YEARS_REQUEST,
    FETCH_CATALOG_YEARS_SUCCESS,
    FETCH_CVI_FAILURE,
    FETCH_CVI_REQUEST,
    FETCH_CVI_SUCCESS,
    PROCESS_CVI_VEHICLES,
    AUTO_EXPAND_SETTLED,
    NEW_VEHICLE_SELECTED,
    PROCESS_SWITCH_VEHICLE,
    SEARCH_VEHICLES_CLICKED,
    SEARCH_VEHICLE_FAILURE,
    SEARCH_VEHICLE_REQUEST,
    SEARCH_VEHICLE_SUCCESS,
    UPDATE_CUSTOM_VEHICLE,
    VEHICLE_CHANGE_FAILURE,
    VEHICLE_CHANGE_SUCCESS,
    CHANGE_VEHICLE_TAB_CHANGED,
    UPDATE_VEHICLE_CUSTOM_DATA,
    SET_SEARCH_QUERY,
    SEARCH_STOCK_NUMBER_REQUEST,
    SEARCH_STOCK_NUMBER_SUCCESS,
    SEARCH_STOCK_NUMBER_FAILURE,
    PROCESS_VEHICLE_AGENT_REQUEST,
    DISMISS_INVENTORY_FALLBACK,
    VEHICLE_AGENT_REQUEST,
    VEHICLE_AGENT_SUCCESS,
    VEHICLE_AGENT_FAILURE,
    SET_AGENT_PROMPT_ATTR,
    VEHICLE_AGENT_FILTER_SUCCESS,
    VEHICLE_AGENT_FILTER_FAILURE,
    SET_OPEN_INCENTIVES_AFTER_CHANGE,
    SET_APPLY_VEHICLE_REQUESTED,
    SET_ADD_NEW_SCENARIO_REQUESTED,
    AI_SEARCH_VIN_INVENTORY_SUCCESS
} from './actions';

import { AGENT_SEARCH_TIMEOUT_STATUS, AI_PROMPT_MODE, AI_REQUEST_TRIGGER } from '../constants';

export const changeVehicleInitialState: ChangeVehicleState = {
    query: '',
    activeFetchCount: 0,
    cviVehicles: undefined,
    searchQuery: undefined,
    isSearchError: false,
    isPermissionError: false,
    showSearchError: false,
    showProcessingError: false,
    summary: undefined,
    catalogYears: [],
    catalogMakes: [],
    catalogModels: [],
    catalogTrims: [],
    catalogVehicles: [],
    showCatalogError: false,
    showBuildVehicleError: false,
    vehicleCustomData: {},
    isStockNumberAvailable: undefined,
    isSearchInProgress: false,
    isAgentSearchInProgress: false,
    isVehicleSearchRequested: false,
    searchPageCount: 1,
    searchCurrentPage: 1,
    searchTotalCount: 0,
    agentResponse: '',
    agentVehicleResponse: [],
    agentSearchTotalCount: 0,
    agentPromptString: '',
    agentPromptMode: AI_PROMPT_MODE.SIMILARITY_SEARCH,
    showAgentProcessingError: false,
    agentSearchTimeoutCount: 0,
    showAgentFilterError: false,
    isAgentFallbackPending: false,
    isShowingInventoryFallback: false
};

export type ChangeVehicleReducer = Reducer<ChangeVehicleState>;

export const reducer: ChangeVehicleReducer = (state = changeVehicleInitialState, action: any) => {
    switch (action.type) {
        case NavigationActions.NAVIGATE_TO:
        case CHANGE_VEHICLE_CANCEL: {
            return changeVehicleInitialState;
        }
        case RESET_ON_ADD_NEW_SCENARIO: {
            return changeVehicleInitialState;
        }
        case UPDATE_CUSTOM_VEHICLE: {
            return {
                ...state,
                customVehicle: action.payload
            };
        }
        case UPDATE_VEHICLE_CUSTOM_DATA: {
            const currentCustomData = state.vehicleCustomData || {};

            return {
                ...state,
                isAutoExpandSettled: true,
                vehicleCustomData: {
                    ...currentCustomData,
                    [action.payload.vin]: action.payload.vehicleCustomData
                }
            };
        }
        case FETCH_CVI_REQUEST:
        case SEARCH_VEHICLE_REQUEST: {
            return {
                ...state,
                activeFetchCount: incrementFetchCount(state),
                cviVehicles: undefined,
                searchQuery: undefined,
                selectedVehicle: {},
                vehicleCustomData: {},
                showSearchError: false,
                showProcessingError: false,
                isPermissionError: false,
                isSearchInProgress: true,
                isVehicleSearchRequested: true
            };
        }
        case FETCH_CVI_SUCCESS:
        case SEARCH_VEHICLE_SUCCESS: {
            const { summary, page, pageCount } = action.payload;

            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                showSearchError: false,
                isSearchInProgress: false,
                isFiltersInitialized: true,
                summary,
                searchCurrentPage: page,
                searchPageCount: pageCount,
                searchTotalCount: summary?.count || 0
            };
        }
        case PROCESS_CVI_VEHICLES: {
            return {
                ...state,
                cviVehicles: action.payload
            };
        }
        case AUTO_EXPAND_SETTLED: {
            if (state.isAutoExpandSettled) {
                return state;
            }

            return {
                ...state,
                isAutoExpandSettled: true
            };
        }
        case FETCH_CVI_FAILURE:
        case SEARCH_VEHICLE_FAILURE: {
            if (action.meta?.isAborted) {
                return {
                    ...state,
                    activeFetchCount: decrementFetchCount(state)
                };
            }

            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                showSearchError: true,
                isSearchInProgress: false,
                searchPageCount: 1,
                searchCurrentPage: 1,
                searchTotalCount: 0
            };
        }
        case SEARCH_STOCK_NUMBER_REQUEST: {
            return {
                ...state,
                activeFetchCount: incrementFetchCount(state),
                isStockNumberAvailable: undefined,
                isSearchInProgress: true,
                isSearchError: false,
                isPermissionError: false
            };
        }
        case SEARCH_STOCK_NUMBER_SUCCESS: {
            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                isStockNumberAvailable: action.payload.vehicles.length === 0,
                isSearchInProgress: false
            };
        }
        case SEARCH_STOCK_NUMBER_FAILURE: {
            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                isSearchError: true,
                isPermissionError: action.meta?.isPermissionError || false,
                isStockNumberAvailable: false,
                isSearchInProgress: false
            };
        }
        case FETCH_CATALOG_YEARS_REQUEST: {
            return {
                ...state,
                activeFetchCount: incrementFetchCount(state),
                showCatalogError: false,
                catalogYears: []
            };
        }
        case FETCH_CATALOG_YEARS_SUCCESS: {
            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                showCatalogError: false,
                catalogYears: mapYears(action.payload)
            };
        }
        case FETCH_CATALOG_YEARS_FAILURE: {
            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                showCatalogError: true
            };
        }
        case FETCH_CATALOG_MAKES_REQUEST: {
            return {
                ...state,
                activeFetchCount: incrementFetchCount(state),
                showCatalogError: false,
                catalogMakes: []
            };
        }
        case FETCH_CATALOG_MAKES_SUCCESS: {
            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                showCatalogError: false,
                catalogMakes: mapMakes(action.payload)
            };
        }
        case FETCH_CATALOG_MAKES_FAILURE: {
            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                showCatalogError: true
            };
        }
        case FETCH_CATALOG_MODELS_REQUEST: {
            return {
                ...state,
                activeFetchCount: incrementFetchCount(state),
                showCatalogError: false,
                catalogModels: []
            };
        }
        case FETCH_CATALOG_MODELS_SUCCESS: {
            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                showCatalogError: false,
                catalogModels: mapModels(action.payload)
            };
        }
        case FETCH_CATALOG_MODELS_FAILURE: {
            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                showCatalogError: true
            };
        }
        case FETCH_CATALOG_TRIMS_REQUEST: {
            return {
                ...state,
                activeFetchCount: incrementFetchCount(state),
                showCatalogError: false,
                catalogTrims: []
            };
        }
        case FETCH_CATALOG_TRIMS_SUCCESS: {
            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                showCatalogError: false,
                catalogTrims: mapTrims(action.payload)
            };
        }
        case FETCH_CATALOG_TRIMS_FAILURE: {
            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                showCatalogError: true
            };
        }
        case FETCH_CATALOG_VEHICLES_REQUEST: {
            return {
                ...state,
                activeFetchCount: incrementFetchCount(state),
                showProcessingError: false,
                showCatalogError: false,
                catalogVehicles: [],
                showBuildVehicleError: false
            };
        }
        case FETCH_CATALOG_VEHICLES_SUCCESS: {
            const catalogVehicles = action.payload.map(catalogToVehicle).filter(({ chromeStyleId }: IVehicle) => chromeStyleId);

            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                showCatalogError: false,
                catalogVehicles,
                showBuildVehicleError: !catalogVehicles.length
            };
        }
        case FETCH_CATALOG_VEHICLES_FAILURE: {
            return {
                ...state,
                activeFetchCount: decrementFetchCount(state),
                showCatalogError: true
            };
        }
        case CLEAR_CATALOG_VEHICLES: {
            return {
                ...state,
                selectedVehicle: {},
                showProcessingError: false,
                showCatalogError: false,
                catalogVehicles: [],
                showBuildVehicleError: false
            };
        }
        case PROCESS_SWITCH_VEHICLE: {
            return {
                ...state,
                isChangeProcessing: true,
                showProcessingError: false,
                isVehicleSearchRequested: false
            };
        }
        case SET_OPEN_INCENTIVES_AFTER_CHANGE: {
            return {
                ...state,
                openIncentivesAfterChange: action.payload
            };
        }
        case SET_APPLY_VEHICLE_REQUESTED: {
            return {
                ...state,
                applyVehicleRequested: action.payload
            };
        }
        case SET_ADD_NEW_SCENARIO_REQUESTED: {
            return {
                ...state,
                addNewScenarioRequested: action.payload
            };
        }
        case NEW_VEHICLE_SELECTED: {
            return {
                ...state,
                isAutoExpandSettled: state.isAutoExpandSettled || !action.meta?.isAutomatic,
                selectedVehicle: action.payload.vehicle
            };
        }
        case VEHICLE_CHANGE_SUCCESS: {
            return {
                ...changeVehicleInitialState,
                isChangeProcessing: false,
                openIncentivesAfterChange: state.openIncentivesAfterChange
            };
        }
        case VEHICLE_CHANGE_FAILURE: {
            return {
                ...state,
                isChangeProcessing: false,
                showProcessingError: true,
                openIncentivesAfterChange: false
            };
        }
        case SEARCH_VEHICLES_CLICKED: {
            return {
                ...state,
                selectedVehicle: {}
            };
        }
        case CHANGE_VEHICLE_TAB_CHANGED: {
            return {
                ...state,
                isAutoExpandSettled: true,
                selectedVehicle: {},
                showCatalogError: false,
                showBuildVehicleError: false,
                showProcessingError: false,
                showAgentProcessingError: false,
                agentSearchTimeoutCount: 0
            };
        }
        case SET_SEARCH_QUERY: {
            return {
                ...state,
                searchQuery: action.payload.searchQuery
            };
        }

        //#region - Vehicle Agent-related cases

        case VEHICLE_AGENT_FILTER_SUCCESS: {
            const agentFilterSummary = normalizeFilterSummary(action.payload?.summary);

            return {
                ...state,
                agentFilterSummary,
                showAgentFilterError: false
            };
        }

        case VEHICLE_AGENT_FILTER_FAILURE: {
            return {
                ...state,
                showAgentFilterError: true
            };
        }

        case PROCESS_VEHICLE_AGENT_REQUEST: {
            if (action.payload?.trigger === AI_REQUEST_TRIGGER.INVENTORY_FALLBACK) {
                return state;
            }

            return {
                ...state,
                isAgentFallbackPending: false,
                isShowingInventoryFallback: false
            };
        }

        case DISMISS_INVENTORY_FALLBACK: {
            return {
                ...state,
                isShowingInventoryFallback: false
            };
        }

        case VEHICLE_AGENT_REQUEST: {
            return {
                ...state,
                agentVehicleResponse: [],
                // Cleared with the results it applied to, so one prompt's answer cannot mark the next's.
                aiSearchInactiveVin: undefined,
                isAgentSearchInProgress: true,
                isVehicleSearchRequested: true
            };
        }

        /**
         * Records a vin live inventory no longer holds, for the selector to mark on the row carrying it.
         * An empty list counts as gone: this endpoint returns none only when it found neither an active
         * record nor a catalog one. An answer for a prompt that has since changed, one whose body was
         * never parsed, and a failed or aborted check all leave the rows as the index described them.
         */
        case AI_SEARCH_VIN_INVENTORY_SUCCESS: {
            const liveVehicles = action.payload?.vehicles;

            // No parsed list is a broken response, not an answer: redux-api-middleware reports success
            // with no payload for any 2xx whose body is not JSON, which a proxy or a login page can be.
            if (!isSameVin(action.meta?.vin, state.agentPromptString) || !Array.isArray(liveVehicles)) {
                return state;
            }

            return {
                ...state,
                aiSearchInactiveVin: isVinInActiveInventory(liveVehicles) ? undefined : action.meta.vin
            };
        }

        case VEHICLE_AGENT_SUCCESS: {
            // Handle new API format with agentResponse field (for chat mode)
            // and model based format with vehicles array (for search mode)
            const responseData = action.payload;

            const agentDecision = responseData?.routing?.decision?.route;

            let agentResponseData: any[] = [];
            let agentResponseText = '';

            if (responseData?.agentResponse !== undefined) {
                // New API format - chat mode response
                agentResponseText = responseData.agentResponse || '';

                // For search mode, vehicles should be in the vehicles array
                // For chat mode, we don't need to extract vehicles from rawTrace as it's conversational
                if (responseData.vehicles && Array.isArray(responseData.vehicles)) {
                    agentResponseData = responseData.vehicles;
                }
            } else {
                // Search-only format fallback - vehicles directly in the response
                agentResponseData = responseData?.vehicles || [];
            }

            // Metadata properties
            const vehicleVin = action.meta?.vehicleVin;
            const vehicleStockNumber = action.meta?.vehicleStockNumber;

            const { agentVehicleResponse, showAgentProcessingError } = processAgentVehicleResponse(
                agentResponseData,
                vehicleVin,
                vehicleStockNumber
            );

            const isAgentDecisionSearch = agentDecision === 'search';
            const agentProcessingError = isAgentDecisionSearch ? showAgentProcessingError : false;

            const isSimilaritySearch = action.meta?.promptMode === AI_PROMPT_MODE.SIMILARITY_SEARCH;

            const newAgentFilterSummary = isSimilaritySearch
                ? state.agentFilterSummary // Initial load - keep original full summary
                : normalizeFilterSummary(responseData?.summary);

            const isInventoryFallbackResponse = action.meta?.trigger === AI_REQUEST_TRIGGER.INVENTORY_FALLBACK;
            const shouldStartInventoryFallback =
                isSimilaritySearch &&
                action.meta.trigger === AI_REQUEST_TRIGGER.DRAWER_OPEN &&
                action.meta.bodyStyleRelaxationEnabled === true &&
                agentVehicleResponse.length === 0;

            return {
                ...state,
                isAgentFallbackPending: shouldStartInventoryFallback,
                isShowingInventoryFallback:
                    isInventoryFallbackResponse && state.isAgentFallbackPending === true && agentVehicleResponse.length > 0,
                isAgentSearchInProgress: false,
                agentSearchTimeoutCount: 0,
                isFiltersInitialized: true,
                agentResponse: agentResponseText,
                agentVehicleResponse,
                agentSearchTotalCount: responseData?.count || 0,
                showAgentProcessingError: responseData ? agentProcessingError : true,
                agentSearchPageCount: responseData?.pageCount || 1,
                agentSearchCurrentPage: responseData?.page || 1,
                agentFilterSummary: newAgentFilterSummary
            };
        }

        case VEHICLE_AGENT_FAILURE: {
            // Check if the request was aborted - if so, don't change isAgentSearchInProgress
            if (action.meta?.isAborted) {
                return state;
            }

            // For actual failures, set isAgentSearchInProgress to false and show error
            return {
                ...state,
                isAgentSearchInProgress: false,
                isAgentFallbackPending: false,
                isShowingInventoryFallback: false,
                showAgentProcessingError: true,
                agentSearchTimeoutCount:
                    action.payload?.status === AGENT_SEARCH_TIMEOUT_STATUS ? (state.agentSearchTimeoutCount || 0) + 1 : 0,
                agentSearchTotalCount: 0
            };
        }

        case SET_AGENT_PROMPT_ATTR: {
            const agentPromptString = action.payload?.promptString;
            const agentPromptMode = action.payload?.promptMode;
            return {
                ...state,
                agentPromptString,
                agentPromptMode
            };
        }
        //#endregion - Vehicle Agent-related cases

        default: {
            return state;
        }
    }
};
